/**
 * The ES domain names its sections twice: once in the URL (`/trading/tasks`)
 * and once on the wire, in the `section` field of the `listen` command. The two
 * vocabularies are not the same — the Dashboard is `tasks` in the URL and
 * `overview` on the wire — and three unrelated places used to hard-code one
 * side or the other: the page that sends the listen, the slices that react to
 * it, and the route gate that decides which section waits for the bootstrap.
 *
 * They all read from here instead, so remapping a section is one edit rather
 * than a silent change in what gets gated (SKY-4581).
 */

/**
 * URL sections that subscribe to a channel of their own. Everything else —
 * Tasks, Reports, the DEX setup page — listens on the Dashboard channel.
 */
const ES_LISTEN_SECTIONS: Record<string, string | undefined> = {
  bots: 'trading',
  balances: 'balances',
};

/**
 * The wire section that carries the ES Dashboard bootstrap: the investor list
 * (`Trader`) and the account's supported exchanges. Slices key their bootstrap
 * status off this value.
 */
export const ES_BOOTSTRAP_LISTEN_SECTION = 'overview';

/**
 * The one URL section held back until that bootstrap has answered.
 *
 * Tasks cannot render anything truthful before the investor list arrives: the
 * ES listener middleware picks the default account from it, and sends users
 * without an API key on to `tasks/new` or `not-found` — so an ungated Tasks
 * page shows an account-less shell it is about to navigate away from.
 *
 * Reports listens on the same wire section but is not gated: it keys off the
 * trader list, which the gate never waited on anyway.
 *
 * The ES listener middleware (`store/trading/slice.ts`) keys its account
 * redirects off this same value, so the section the gate holds back and the
 * section the redirect releases it into cannot drift apart.
 */
export const ES_BOOTSTRAP_SECTION = 'tasks';

/** The wire section a URL section subscribes to. */
export const getEsListenSection = (section?: string | null): string =>
  ES_LISTEN_SECTIONS[section ?? ''] ?? ES_BOOTSTRAP_LISTEN_SECTION;
